import { Obstacle, Sensor } from '../api/models';
import { AlignmentConnectivityResponse } from '../api/models';

const sensorPosition = (sensor: Sensor): [number, number, number] => {
  if (sensor.designPosition) {
    return sensor.designPosition;
  }
  return sensor.position as [number, number, number];
};

const xyDistance = (a: [number, number], b: [number, number]) => {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  return Math.sqrt(dx * dx + dy * dy);
};

/** Nearest alignable sensor to world origin in XY. */
export const nearestSensorToOrigin = (sensors: Sensor[]): string | null => {
  const alignable = sensors.filter((s) => s.pcdUrl);
  if (alignable.length === 0) {
    return null;
  }

  let bestId: string | null = null;
  let bestDist = Infinity;
  for (const sensor of alignable) {
    const pos = sensorPosition(sensor);
    const dist = xyDistance([pos[0], pos[1]], [0, 0]);
    if (dist < bestDist) {
      bestDist = dist;
      bestId = sensor.id;
    }
  }
  return bestId;
};

const rayIntersectsAabb = (
  origin: [number, number, number],
  target: [number, number, number],
  obstacle: Obstacle
): boolean => {
  const dir: [number, number, number] = [
    target[0] - origin[0],
    target[1] - origin[1],
    target[2] - origin[2],
  ];
  const dist = Math.sqrt(dir[0] ** 2 + dir[1] ** 2 + dir[2] ** 2);
  if (dist < 1e-6) {
    return false;
  }
  const inv: [number, number, number] = [dir[0] / dist, dir[1] / dist, dir[2] / dist];

  const half = obstacle.size.map((v) => v / 2) as [number, number, number];
  const min: [number, number, number] = [
    obstacle.position[0] - half[0],
    obstacle.position[1] - half[1],
    obstacle.position[2] - half[2],
  ];
  const max: [number, number, number] = [
    obstacle.position[0] + half[0],
    obstacle.position[1] + half[1],
    obstacle.position[2] + half[2],
  ];

  let tMin = 0;
  let tMax = dist;
  const axes: Array<'x' | 'y' | 'z'> = ['x', 'y', 'z'];
  for (let i = 0; i < 3; i += 1) {
    const axis = axes[i];
    const o = origin[i];
    const d = inv[i];
    if (Math.abs(d) < 1e-8) {
      if (o < min[i] || o > max[i]) {
        return false;
      }
      continue;
    }
    const t1 = (min[i] - o) / d;
    const t2 = (max[i] - o) / d;
    tMin = Math.max(tMin, Math.min(t1, t2));
    tMax = Math.min(tMax, Math.max(t1, t2));
    if (tMin > tMax) {
      return false;
    }
  }
  return tMin < dist - 0.05;
};

const hasLineOfSight = (
  origin: [number, number, number],
  target: [number, number, number],
  obstacles: Obstacle[]
): boolean => {
  if (obstacles.length === 0) {
    return true;
  }
  return !obstacles.some((obstacle) => rayIntersectsAabb(origin, target, obstacle));
};

const referenceSensorId = (
  originId: string,
  placedIds: string[],
  activeId: string | null
): string => {
  if (activeId) {
    return activeId;
  }
  if (placedIds.length > 0) {
    return placedIds[placedIds.length - 1];
  }
  return originId;
};

export interface AlignmentConnectivityInput {
  sensors: Sensor[];
  obstacles: Obstacle[];
  originSensorId?: string | null;
  placedSensorIds?: string[];
  activeSensorId?: string | null;
  excludedSensorIds?: string[];
  requireLos?: boolean;
}

export const computeAlignmentConnectivityLocal = (
  input: AlignmentConnectivityInput
): AlignmentConnectivityResponse => {
  const {
    sensors,
    obstacles,
    originSensorId,
    placedSensorIds,
    activeSensorId,
    excludedSensorIds,
    requireLos = true,
  } = input;

  const alignable = sensors.filter((s) => s.pcdUrl);
  if (alignable.length === 0) {
    return {
      origin_sensor_id: null,
      placed_count: 0,
      total_alignable: 0,
      recommended_next: null,
      candidates: [],
      connectivity_source: 'client_fallback',
    };
  }

  const alignableById = new Map(alignable.map((s) => [s.id, s]));
  const placed = (placedSensorIds ?? []).filter((id) => alignableById.has(id));
  const derivedPlaced =
    placed.length > 0
      ? placed
      : alignable.filter((s) => s.alignmentStatus === 'aligned').map((s) => s.id);

  const originId = originSensorId ?? nearestSensorToOrigin(alignable) ?? alignable[0].id;
  const refId = referenceSensorId(originId, derivedPlaced, activeSensorId ?? null);
  const refSensor = alignableById.get(refId) ?? alignableById.get(originId)!;
  const refPos = sensorPosition(refSensor);

  const excluded = new Set(excludedSensorIds ?? []);
  let unplaced = alignable.filter((s) => !derivedPlaced.includes(s.id));
  if (activeSensorId) {
    unplaced = unplaced.filter((s) => s.id !== activeSensorId);
  }
  if (excluded.size > 0) {
    unplaced = unplaced.filter((s) => !excluded.has(s.id));
  }

  const candidates = unplaced
    .map((sensor) => {
      const targetPos = sensorPosition(sensor);
      const distance_m = xyDistance([refPos[0], refPos[1]], [targetPos[0], targetPos[1]]);
      const has_los = hasLineOfSight(refPos, targetPos, obstacles);
      const score = has_los ? 1 / (1 + distance_m) : 0;
      return {
        sensor_id: sensor.id,
        sensor_name: sensor.name,
        distance_m: Math.round(distance_m * 1000) / 1000,
        has_los,
        score: Math.round(score * 10000) / 10000,
      };
    })
    .sort((a, b) => {
      if (a.has_los !== b.has_los) {
        return a.has_los ? -1 : 1;
      }
      return a.distance_m - b.distance_m;
    });

  let recommended_next = null;
  if (candidates.length > 0) {
    if (requireLos) {
      recommended_next = candidates.find((c) => c.has_los) ?? candidates[0];
    } else {
      recommended_next = candidates[0];
    }
  }

  return {
    origin_sensor_id: originId,
    placed_count: derivedPlaced.length,
    total_alignable: alignable.length,
    recommended_next,
    candidates,
    connectivity_source: 'client_fallback',
  };
};

export const nextAlignmentCandidateId = (
  connectivity: AlignmentConnectivityResponse | null
): string | null => connectivity?.recommended_next?.sensor_id ?? null;
